import { useCallback } from 'react';

import type { UserGroupColor, UserGroupIcon } from '@frontend/api';

import { useUserGroupCollectionContext } from '../UserGroupCollectionProvider';

type UserGroupBadgeProps = {
  name: string;
  colorPalette: UserGroupColor;
  icon: UserGroupIcon;
};

type UserGroupBadgeLookup = (ownerId: number) => UserGroupBadgeProps | null;

/**
 * Provides a lookup function that maps an owner_id to UserGroupBadge props.
 * Fetches all groups in the collection so badges render for every group,
 * including ones the current user is not a direct member of (superuser case).
 * Returns a no-op that always returns null when the multisite feature is
 * disabled or the active group is not part of a collection.
 */
export const useUserGroupBadgeLookup = (): UserGroupBadgeLookup => {
  const { getUserGroupById } = useUserGroupCollectionContext();

  return useCallback(
    (ownerId: number) => {
      const group = getUserGroupById(ownerId);
      return group
        ? {
            name: group.businessName,
            colorPalette: group.color,
            icon: group.icon,
          }
        : null;
    },
    [getUserGroupById],
  );
};
